import { FALLBACK_PROPERTY_CONDITIONS } from '../constants/propertyConditionFallback'

const CONDITION_FILTER_VALUES = Object.fromEntries(
  FALLBACK_PROPERTY_CONDITIONS.filter(({ filterKey }) => filterKey).map(({ filterKey, value }) => [filterKey, value])
)

// Parse URL parameters into filter object
export const parseUrlFilters = (location) => {
  if (!location) {
    return {
      districts: [],
      municipalities: [],
      layouts: [],
      displayOptions: [],
      priceRange: { min: 0, max: 30000000 },
    }
  }

  const queryParams = new URLSearchParams(location.search)

  return {
    districts: queryParams.get('districts') ? queryParams.get('districts').split(',') : [],
    municipalities: queryParams.get('municipalities') ? queryParams.get('municipalities').split(',') : [],
    layouts: parseLayoutsFromUrl(queryParams.get('layouts')),
    displayOptions: queryParams.get('options') ? queryParams.get('options').split(',') : [],
    priceRange: {
      min: queryParams.get('minPrice') ? parseInt(queryParams.get('minPrice')) : 0,
      max: queryParams.get('maxPrice') ? parseInt(queryParams.get('maxPrice')) : 30000000,
    },
  }
}

// Convert URL layout format (2kk) to UI format (2+kk)
export const parseLayoutsFromUrl = (layoutsParam) => {
  if (!layoutsParam) return []

  return layoutsParam.split(',').map((layout) => {
    const match = layout.match(/^(\d)(kk|\d)$/i)
    if (match) {
      return `${match[1]}+${match[2]}`
    }
    return layout
  })
}

// Convert UI layout format (2+kk) to URL format (2kk)
export const convertLayoutsToUrl = (layouts) => {
  return layouts.map((layout) => {
    const match = layout.match(/^(\d)\+(kk|\d)$/i)
    if (match) {
      return `${match[1]}${match[2]}`
    }
    return layout
  })
}

// Build URL query string from filters
export const buildUrlFilters = (filters) => {
  const params = new URLSearchParams()

  if (filters.districts?.length > 0) {
    params.set('districts', filters.districts.join(','))
  }

  if (filters.municipalities?.length > 0) {
    params.set('municipalities', filters.municipalities.join(','))
  }

  if (filters.layouts?.length > 0) {
    params.set('layouts', convertLayoutsToUrl(filters.layouts).join(','))
  }

  if (filters.displayOptions?.length > 0) {
    params.set('options', filters.displayOptions.join(','))
  }

  if (filters.priceRange) {
    if (filters.priceRange.min > 0) {
      params.set('minPrice', filters.priceRange.min.toString())
    }
    if (filters.priceRange.max < 30000000) {
      params.set('maxPrice', filters.priceRange.max.toString())
    }
  }

  return params.toString()
}

// Apply filters to data
export const applyFilters = (data, filters) => {
  if (!data || !Array.isArray(data)) {
    return []
  }

  if (!filters) {
    return data
  }

  return data
    .filter((item) => item && typeof item === 'object') // Filter out null/undefined items
    .filter((item) => item.type === filters.type)
    .filter((item) => {
      // District filtering
      if (!filters.districts || filters.districts.length === 0) return true
      if (!item.location) return false

      // Extract district from location (e.g. "Brno - Žabovřesky" -> "Žabovřesky")
      const locationParts = item.location.split(' - ')
      if (locationParts.length < 2) return false

      const itemDistrict = locationParts[1].trim().toLowerCase()
      return filters.districts.some((district) => district.toLowerCase() === itemDistrict)
    })
    .filter((item) => !filters.displayOptions?.includes('hideNoPrice') || item.price !== null)
    .filter((item) => !filters.displayOptions?.includes('hideInactive') || !item.inactive)
    .filter((item) => {
      // Price range filtering
      if (!filters.priceRange) return true
      if (!item.price) return true // Skip items without price

      const { min, max } = filters.priceRange
      return item.price >= min && item.price <= max
    })
    .filter((item) => {
      if (!item.ownership) return !filters.displayOptions?.includes('osobní')
      const ownership = item.ownership.toLowerCase().split(',')[0]
      return filters.displayOptions?.includes(ownership) || !filters.displayOptions?.includes('osobní')
    })
    .filter((item) => (filters.displayOptions?.includes('showComments') && item.comments) || !filters.displayOptions?.includes('showComments'))
    .filter((item) => (filters.displayOptions?.includes('showAnalysis') && item.analyza) || !filters.displayOptions?.includes('showAnalysis'))
    .filter((item) => {
      if (!filters.displayOptions) return true

      const stavbaFilters = filters.displayOptions.filter((option) => option.startsWith('stavba-'))
      if (stavbaFilters.length === 0) return true

      if (!item.stavba) return false // Skip if no stavba property

      return stavbaFilters.some((filter) => CONDITION_FILTER_VALUES[filter] && item.stavba.includes(CONDITION_FILTER_VALUES[filter]))
    })
    .filter((item) => {
      if (!filters.layouts || filters.layouts.length === 0) return true
      if (!item.title) return false

      const layoutRegex = /\d\+(?:kk|[1-9])/i
      const match = item.title.match(layoutRegex)
      if (!match) return false

      const foundLayout = match[0].toLowerCase()
      return filters.layouts.some((layout) => layout.toLowerCase() === foundLayout)
    })
}
